import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

export type RecentType =
  | 'customer'
  | 'payment'
  | 'subscription'
  | 'booking'
  | 'submission'
  | 'checkout-page';

export interface RecentEntry {
  type: RecentType;
  id: string;
  label: string;
  subtitle?: string;
  path: string;
  visitedAt: number;
}

const MAX_RECENTS = 10;

function storageKey(userId: string, sellerId: string) {
  return `cmdk:recents:${userId}:${sellerId}`;
}

function readStorage(key: string): RecentEntry[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useRecents() {
  const { userId, sellerId } = useAuth();
  const key = userId && sellerId ? storageKey(userId, sellerId) : null;
  const [recents, setRecents] = useState<RecentEntry[]>(() => (key ? readStorage(key) : []));

  useEffect(() => {
    setRecents(key ? readStorage(key) : []);
  }, [key]);

  const addRecent = useCallback(
    (entry: RecentEntry) => {
      if (!key) return;
      setRecents(prev => {
        const without = prev.filter(e => !(e.type === entry.type && e.id === entry.id));
        const next = [entry, ...without].slice(0, MAX_RECENTS);
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* storage full/denied — ignore */
        }
        return next;
      });
    },
    [key],
  );

  return { recents, addRecent };
}
